import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../lib/auth.context'

interface TOTPEnrollmentProps {
  onComplete?: () => void
  redirectTo?: string
}

export function TOTPEnrollment({ onComplete, redirectTo }: TOTPEnrollmentProps) {
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation()
  const { setIsTOTPVerified } = useAuth()

  useEffect(() => {
    const setupTOTP = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user?.email) throw new Error('User email not found')

        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `${t('title')} - ${user.email}`
        })

        if (error) throw error

        if (data.totp) {
          setQrCode(data.totp.qr_code)
          setSecret(data.totp.secret)
          setFactorId(data.id)
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to setup TOTP'
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    setupTOTP()
  }, [toast, t])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })

      if (error) throw error

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode
      })

      if (verifyError) throw verifyError

      toast({
        title: 'Success',
        description: 'TOTP device enrolled successfully',
      })

      // Refresh the session to ensure we have the latest token
      const { data: { session: newSession }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      // Ensure we have a valid session before redirecting
      if (!newSession?.access_token) {
        throw new Error('Failed to initialize session after TOTP enrollment')
      }

      // Set TOTP verification status after successful enrollment
      console.log('[TOTPEnrollment] Setting TOTP verification to true after successful enrollment')
      setIsTOTPVerified(true)

      if (onComplete) {
        onComplete()
      }

      // Show success message and redirect to admin dashboard
      toast({
        title: 'Setup Complete',
        description: 'Your account has been set up successfully. Redirecting to dashboard...',
      })

      // If redirecting to club-ui, store the access token
      if (redirectTo?.startsWith('http://') || redirectTo?.startsWith('https://')) {
        console.log('[TOTPEnrollment] Storing access token for club-ui redirect:', {
          redirectTo,
          hasToken: !!newSession.access_token
        })
        localStorage.setItem('club_access_token', newSession.access_token)
      } else {
        console.log('[TOTPEnrollment] Not storing token - internal redirect:', { redirectTo })
      }

      // Short delay to show the success message
      setTimeout(() => {
        if (redirectTo) {
          // Check if it's an external URL (starts with http:// or https://)
          if (redirectTo.startsWith('http://') || redirectTo.startsWith('https://')) {
            // Add both tokens as query parameters for external redirects
            const redirectUrl = new URL(redirectTo)
            redirectUrl.searchParams.set('access_token', newSession.access_token)
            redirectUrl.searchParams.set('refresh_token', newSession.refresh_token)
            console.log('[TOTPEnrollment] Redirecting to club-ui with tokens')
            window.location.href = redirectUrl.toString()
          } else {
            navigate(redirectTo)
          }
        } else {
          navigate('/admin/dashboard')
        }
      }, 1500)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
      <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>
        Set Up Two-Factor Authentication
      </h1>
      <div className="space-y-4">
        <div className="text-center">
          {qrCode && (
            <div className="mb-4">
              <img src={qrCode} alt="QR Code" className="mx-auto" />
            </div>
          )}
          {secret && (
            <div className="mb-4">
              <p className="text-sm text-gray-600">Manual entry code:</p>
              <code className="block p-2 bg-gray-100 rounded">{secret}</code>
            </div>
          )}
          <p className={`mb-4 ${getThemeClass('components.text.body')}`}>
            Scan the QR code or enter the secret key manually in your authenticator app
          </p>
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <Input
              type="text"
              placeholder="Enter verification code"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              required
              disabled={isLoading}
              className={getThemeClass('components.input.base')}
            />
          </div>
          {formError && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <Button
            type="submit"
            className={`w-full ${getThemeClass('components.button.primary')}`}
            disabled={isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify and Complete Setup'}
          </Button>
        </form>
      </div>
    </Card>
  )
}
